/**
 * Role-Based Access Control (RBAC) utilities
 * Prevents borrowers from accessing lender dashboard and vice versa
 */

import { jwtDecode } from "jwt-decode";
import { getTokens, setTokens, clearTokens } from "./secureTokenStorage";

export const ROLES = {
  LENDER: "Lender",
  BORROWER: "Borrower",
};

export const PATHS = {
  LENDER: "/",
  BORROWER: "/borrower",
};

/**
 * Get user role from stored authentication tokens
 * @returns {string|null} User role or null if not authenticated
 */
export function getUserRole() {
  try {
    // First check lender session storage (most authoritative for lenders)
    const lenderSession = window.sessionStorage.getItem("cm_user_session") || window.localStorage.getItem("cm_user_session");
    if (lenderSession) {
      const parsed = JSON.parse(lenderSession);
      if (parsed?.role) {
        return parsed.role.charAt(0).toUpperCase() + parsed.role.slice(1); // Normalize to "Lender" or "Borrower"
      }
    }

    // Then check auth tokens (used by both systems)
    const tokens = getTokens();

    if (tokens) {

      // Check if role is directly in tokens
      if (tokens?.role) {
        return tokens.role;
      }

      // Try to decode JWT to get role
      const token = tokens?.accessToken;
      if (token) {
        const decoded = jwtDecode(token);
        return decoded?.role || null;
      }
    }

    return null;
  } catch (error) {
    console.error("Error reading user role:", error);
    return null;
  }
}

/**
 * Check if current user has required role
 * @param {string} requiredRole - Required role (LENDER or BORROWER)
 * @returns {boolean} True if user has required role
 */
export function hasRole(requiredRole) {
  const currentRole = getUserRole();
  return currentRole === requiredRole;
}

/**
 * Check if user is authorized to access lender dashboard
 * @returns {boolean} True if authorized
 */
export function canAccessLender() {
  const role = getUserRole();
  return role === ROLES.LENDER || role === null; // Allow unauthenticated to see landing page
}

/**
 * Check if user is authorized to access borrower dashboard
 * @returns {boolean} True if authorized
 */
export function canAccessBorrower() {
  const role = getUserRole();
  return role === ROLES.BORROWER; // Require authentication for borrower portal
}

/**
 * Redirect user to appropriate dashboard based on their role
 * @param {string} currentPath - Current URL path
 */
export function enforceRoleAccess(currentPath) {
  const role = getUserRole();

  const isBorrowerPath = currentPath.startsWith(PATHS.BORROWER);
  const isLenderPath = !isBorrowerPath;

  // Unauthenticated user trying to access borrower portal - redirect to login
  if (!role && isBorrowerPath) {
    console.warn("Unauthenticated user attempting to access borrower portal - redirecting to login");
    window.location.href = PATHS.LENDER; // Redirect to lender landing/login page
    return;
  }

  // Allow unauthenticated users on lender path (they'll see landing page)
  if (!role && isLenderPath) return;

  // Borrower trying to access lender dashboard
  if (role === ROLES.BORROWER && isLenderPath) {
    console.warn("Borrower attempting to access lender dashboard - redirecting");
    window.location.href = PATHS.BORROWER;
    return;
  }

  // Lender trying to access borrower dashboard
  if (role === ROLES.LENDER && isBorrowerPath) {
    console.warn("Lender attempting to access borrower dashboard - redirecting");
    window.location.href = PATHS.LENDER;
    return;
  }
}

/**
 * Store user role securely after authentication
 * @param {string} role - User role
 * @param {Object} tokens - Authentication tokens
 * @param {boolean} rememberMe - Whether to use localStorage (vs sessionStorage)
 */
export function storeAuthWithRole(role, tokens, rememberMe = false) {
  // Ensure role is included in tokens
  const tokensWithRole = {
    ...tokens,
    role,
  };

  setTokens(tokensWithRole, rememberMe);
}

/**
 * Clear authentication and role data
 */
export function clearAuth() {
  clearTokens();
  // Clear lender session storage
  window.localStorage.removeItem("cm_user_session");
  window.sessionStorage.removeItem("cm_user_session");
  // Also clear any other auth-related storage
  window.localStorage.removeItem("cm_user");
  window.sessionStorage.removeItem("cm_user");
}

/**
 * Get the correct redirect path for a given role
 * @param {string} role - User role
 * @returns {string} Redirect path
 */
export function getPathForRole(role) {
  if (role === ROLES.BORROWER) return PATHS.BORROWER;
  if (role === ROLES.LENDER) return PATHS.LENDER;
  return PATHS.LENDER; // Default to lender landing page
}
